// import Vue from 'vue'
// import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import jwtDefaultConfig from './jwtDefaultConfig'
/* eslint-disable-next-line */
import router from '@/router'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  static persistRedirectAfterLogin() {
    const { name: routeName, fullPath } = router.currentRoute || {}

    if (!fullPath) return
    if (routeName === 'auth-login') return

    localStorage.setItem('redirectAfterLogin', fullPath)
  }

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        // const vm = new Vue()
        // const { config, response: { status } } = error
        const { config, response } = error
        const originalRequest = config

        const isExpiredToken = response && response.status === 401 && response.data?.error_subcode === 463

        if (isExpiredToken) {
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            this.refreshToken()
              .then(r => {
                // Update accessToken in localStorage
                this.setToken(r.data.token)
                this.setRefreshToken(r.data.refresh_token)

                this.onAccessTokenFetched(r.data.token)
              })
              .catch(() => {
                this.clearSession()
                this.onAccessTokenFetched(null)
              })
              .finally(() => {
                this.isAlreadyFetchingAccessToken = false
              })
          }
          const retryOriginalRequest = new Promise((resolve, reject) => {
            this.addSubscriber(accessToken => {
              if (!accessToken) {
                reject(error)
                return
              }

              // Make sure to assign accessToken according to your response.
              // Check: https://pixinvent.ticksy.com/ticket/2413870
              // Change Authorization header
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(this.axiosIns(originalRequest))
            })
          })
          return retryOriginalRequest
        }
        // Un 401 puede venir de una peticion que salio con un token anterior: por ejemplo el
        // sondeo de notificaciones, que sigue corriendo en la pantalla de login y responde
        // justo despues de que el usuario entro. Tumbar la sesion ahi mata la que se acaba
        // de crear y obliga a iniciar sesion dos veces.
        if (response && response.status === 401 && this.isStaleRequest(originalRequest)) {
          return Promise.reject(error)
        }

        if (response && response.status === 401) {
          // vm.$toast({
          //   component: ToastificationContent,
          //   props: {
          //     title: 'Error',
          //     icon: 'AlertCircleIcon',
          //     variant: 'danger',
          //     text: 'Token expirado',
          //   },
          // })
          this.clearSession()
        }
        return Promise.reject(error)
      },
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  // true cuando la peticion que fallo se mando con un token distinto al que hay guardado
  // ahora, o sea que su 401 ya no dice nada sobre la sesion vigente.
  isStaleRequest(originalRequest) {
    const sentToken = originalRequest?.headers?.Authorization
    const currentToken = this.getToken()

    if (!sentToken || !currentToken) return false

    return sentToken !== `${this.jwtConfig.tokenType} ${currentToken}`
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  // Una ruta publica (la liga de cotizacion, la de pago) se ve sin cuenta. Si el navegador
  // trae un token viejo el backend responde 401, pero mandar a login ahi deja la liga
  // inservible para cualquiera que haya usado la plataforma antes.
  static isPublicRoute() {
    const route = router.currentRoute
    if (!route?.name) return false

    return route.meta?.requiresAuth === false
  }

  clearSession() {
    // Estando en la pantalla de login no hay sesion que proteger, y en cambio se puede
    // borrar la que el usuario acaba de crear si un 401 rezagado llega en ese instante.
    if (router.currentRoute?.name === 'auth-login') return

    localStorage.removeItem('redirectAfterLogin')
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    localStorage.removeItem('userData')
    // El proyecto se hidrata de localStorage al arrancar el store y no se borraba al cerrar
    // sesion, asi que el siguiente usuario del navegador heredaba el proyecto del anterior:
    // el navbar le mostraba el nombre de la obra de otro cliente.
    localStorage.removeItem('project')

    if (JwtService.isPublicRoute()) return

    router.replace({ name: 'auth-login' })
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      auth: {
        refresh_token: this.getRefreshToken(),
        token: this.getToken(),
      },
    })
  }
}
